/**
 * healthcareImportTemplate: the PHARMACY workbook the operator starts from.
 * ───────────────────────────────────────────────────────────────────────────
 * The layout (READ ME, Column Guide, the two data sheets, the hint row above
 * each header, the self-filling formula fill-down, the Lists sheet and the
 * download plumbing) is catalogueImportTemplate.js, shared with the brand
 * importer. The columns come from healthcareImportColumns.js, the same list
 * healthcareImport.js reads headers with, so the template and the parser
 * cannot disagree. What is left here is the pharmacy's own prose, its own
 * reference lists and its own example rows.
 *
 * SAME BLUEPRINT AS CHINA TOWN. Izi settled the shape of a Lusuku upload sheet
 * on 26 Sep 2026 and decided the other verticals follow it: Title Case
 * headers, a one-line hint above each, a Column Guide, a Status column, and
 * columns that fill themselves in. A pharmacist who has filled a brand sheet
 * is filling the same sheet here.
 *
 * TWO EXAMPLE ROWS RATHER THAN ONE, and it is worth the extra row: the
 * commonest mistake on this sheet is counting a consultation in Count in Stock
 * instead of Available Slots, so the sheet ships with one Product and one
 * Service side by side. Both carry the marker string the parser drops, so
 * neither can import itself.
 */
import * as XLSX from "xlsx";
import {
  NAMES,
  PRODUCTS_SHEET,
  PRODUCT_COLUMNS,
  PRODUCT_FORMULAS,
  SECTIONS_SHEET,
  SECTION_COLUMNS,
  SECTION_FORMULAS,
  STATUS,
  columnLetters,
  productRange,
} from "./healthcareImportColumns.js";
import { BATCH_EXAMPLE, PRODUCT_TYPES, VAT_CATEGORIES } from "./healthcareImportColumns.js";
import { PAYMENT_METHODS } from "./catalogueImport.js";
import {
  EXAMPLE_NOTE,
  FIRST_DATA_ROW,
  GUIDE_SHEET,
  LISTS_SHEET,
  PRODUCT_FORMULA_ROWS,
  README_SHEET,
  SECTION_FORMULA_ROWS,
  SHARED_LIST_ROWS,
  YES_NO_LIST_ROWS,
  buildGuideRows,
  dataSheet,
  downloadWorkbook,
  templateFileNameFor,
  textSheet,
  PHOTO_README_ROWS,
  STAND_IN_NOTE,
} from "./catalogueImportTemplate.js";

/** Dropped by the parser. Re-exported so one import path serves both. */
export { EXAMPLE_NOTE } from "./catalogueImportTemplate.js";

const README = (vendorName) => {
  const rows = [
    [`Lusuku bulk product import${vendorName ? `: ${vendorName}` : ""}`],
    [],
    ["Fill in the Sections and Products sheets, then upload this file on the pharmacy's Menu Management page."],
    [],
    ["HOW IT WORKS"],
    ["1.", `Put every product and service on the '${PRODUCTS_SHEET}' sheet, one row each. Section and Name are required.`],
    ["2.", "The To Do column says what each row still needs before it can go live. It updates as you type."],
    ["3.", "Image Source fills itself in: naming a real photo clears Placeholder."],
    ["4.", "Filter the Status column to see Active or Deactivated rows."],
    ["5.", "Upload the workbook and the photo files together. Photos are matched to Image File, ignoring capitalisation."],
    ["6.", "Nothing is written until you have seen the review table and pressed Import."],
    ["7.", `The '${GUIDE_SHEET}' sheet explains every column.`],
    [],
    ["SETTINGS"],
  ];
  const standInRow = rows.length + 1; // 1-based row of the setting below
  rows.push(["Stand-in cover file", ""]);
  rows.push(["Type the file name of your stand-in cover photo, if you have one. Rows using it are marked Placeholder and show 'Needs photo' on Lusuku."]);
  rows.push([STAND_IN_NOTE]);
  rows.push(
    [],
    ...PHOTO_README_ROWS,
    [],
    ["PRODUCTS AND SERVICES"],
    ["·", "A Product is something on a shelf. Its stock goes in Count in Stock."],
    ["·", "A Service is a consultation or procedure. Its capacity goes in Available Slots, and Count in Stock is ignored."],
    ["·", "Leave Product or Service blank and the row is treated as a Product."],
    [],
    ["THE REGULATED COLUMNS"],
    ["·", "Prescription Only is a clinical fact. It does NOT change tax. A prescription drug is standard-rated unless you say otherwise in VAT Treatment."],
    ["·", "Controlled Substance and Schedule belong together. A Schedule with the box unticked leaves the item out of the controlled-substance register."],
    ["·", `Batches are written as ${BATCH_EXAMPLE}, one per line in the cell. The expiry must be a real date.`],
    ["·", "An expired batch still imports. The counter needs to see it, and the terminal refuses to sell it."],
    ["·", "A batch Lusuku cannot read stops the whole row rather than being dropped quietly."],
    [],
    ["THE RULES THAT BITE"],
    ["·", "Every row needs a cover image. There is no way to create one without a photo."],
    ["·", "Price (UGX) is the GROSS price a customer pays. Never enter your net price; Lusuku calculates commission from the gross."],
    ["·", "Nothing may sell at 0. Leave Price on Request blank and fill the price in, or set it to Yes for an item you genuinely quote."],
    ["·", "Rows marked Out of Stock are skipped, not imported as zero-stock products."],
    ["·", "Re-running the same workbook is safe. Anything already on this pharmacy is skipped, matched on Barcode."],
    ["·", "Names may repeat inside a section when the Barcodes differ."],
    ["·", "Imports land DEACTIVATED by default. Activate rows once prices, batches and photos are confirmed."],
    ["·", "Delete the example rows before importing. They are skipped anyway: they carry a marker in their notes."],
    [],
    ["WHAT IS NOT HERE, AND WHY"],
    ["·", "Commission, vendor net price and country have no columns. Lusuku calculates all three."],
    ["·", "There are no Retailer / Wholesaler / Healthcare price columns. Healthcare orders are priced from Price (UGX) alone, so a tier price here would be overwritten without telling you. Quote a trade buyer through Price on Request instead."],
    ["·", "URA's commodity code and unit of measure are set by Lusuku, not by the vendor."]
  );
  return { rows, standInRef: `'${README_SHEET}'!$B$${standInRow}` };
};

const LISTS = (categoryNames, unitNames) => {
  const rows = [
    ...SHARED_LIST_ROWS(PAYMENT_METHODS),
    ["Product or Service"],
    ...PRODUCT_TYPES.map((t) => [t]),
    [],
    ["VAT Treatment"],
    ...VAT_CATEGORIES.map((v) => [v]),
    [],
    ...YES_NO_LIST_ROWS,
    ["Batches"],
    [`One batch per line, written as ${BATCH_EXAMPLE}`],
    ["Expiry dates are written YYYY-MM-DD."],
    [],
  ];
  rows.push(["Platform Categories"]);
  if (categoryNames.length) for (const c of categoryNames) rows.push([c]);
  else rows.push(["Loaded from Lusuku when you download this from a pharmacy page."]);
  rows.push([]);
  rows.push(["Unit"]);
  if (unitNames.length) for (const u of unitNames) rows.push([u]);
  else rows.push(["Loaded from Lusuku when you download this from a pharmacy page."]);
  return rows;
};

/**
 * Build the workbook.
 *
 * Called with no arguments it produces the generic blank preset. Called from a
 * pharmacy's page it produces the SAME file with that vendor's existing
 * sections already listed, which is the difference between "here is a
 * spreadsheet" and "here is your spreadsheet".
 *
 * @param {object}   opts
 * @param {string}   [opts.vendorName]
 * @param {string[]} [opts.sectionTitles]  sections the pharmacy already has
 * @param {string[]} [opts.categoryNames]  live Lusuku category names
 * @param {string[]} [opts.unitNames]      live Lusuku unit names
 * @returns an xlsx workbook object
 */
export const buildTemplateWorkbook = ({
  vendorName = "",
  sectionTitles = [],
  categoryNames = [],
  unitNames = [],
} = {}) => {
  const wb = XLSX.utils.book_new();

  const readme = README(vendorName);
  XLSX.utils.book_append_sheet(wb, textSheet(readme.rows), README_SHEET);

  XLSX.utils.book_append_sheet(
    wb,
    textSheet(buildGuideRows(SECTION_COLUMNS, PRODUCT_COLUMNS), [
      { wch: 14 },
      { wch: 24 },
      { wch: 16 },
      { wch: 90 },
    ]),
    GUIDE_SHEET
  );

  // ── 1. Sections ──────────────────────────────────────────────────────────
  const productRowsEnd = FIRST_DATA_ROW + PRODUCT_FORMULA_ROWS - 1;
  const P = productRange(FIRST_DATA_ROW, productRowsEnd);
  const S = columnLetters(SECTION_COLUMNS);
  const sectionRows = sectionTitles.length
    ? // The pharmacy's existing sections, so an operator adding to a live
      // catalogue spells them the same way and the importer reuses them
      // instead of creating a near-duplicate.
      sectionTitles.map((title, i) => ({
        section_order: i + 1,
        section_title: title,
        notes: "Already on this pharmacy",
      }))
    : [
        {
          section_order: 1,
          section_title: "Example Section",
          section_description: "What belongs in it",
          notes: EXAMPLE_NOTE,
        },
      ];
  XLSX.utils.book_append_sheet(
    wb,
    dataSheet(
      SECTION_COLUMNS,
      [],
      sectionRows,
      (key, r) => SECTION_FORMULAS[key]?.(S, r, P),
      SECTION_FORMULA_ROWS
    ),
    SECTIONS_SHEET
  );

  // ── 2. Products ──────────────────────────────────────────────────────────
  const L = columnLetters(PRODUCT_COLUMNS);
  // The example rows are keyed by field key so they cannot drift out of column
  // order when a column is inserted above.
  const examples = [
    {
      row_id: 1,
      section_title: sectionTitles[0] || "Example Section",
      name: "Example Tablets 500 mg, 20 pack",
      item_code: "EX-01",
      sku_code: "EXAMPLE-001",
      enabled: STATUS.DEACTIVATED,
      description: "One or two lines a shopper would read.",
      product_type: "PRODUCT",
      price_ugx: 12000,
      vat_category: "STANDARD",
      availability: "In Stock",
      count_in_stock: 40,
      image_filename: "example-tablets.jpg",
      min_order_qty: 1,
      requires_prescription: "No",
      batches: BATCH_EXAMPLE,
      notes: EXAMPLE_NOTE,
    },
    {
      row_id: 2,
      section_title: sectionTitles[0] || "Example Section",
      name: "Example Consultation, 20 minutes",
      item_code: "EX-02",
      enabled: STATUS.DEACTIVATED,
      description: "What the patient gets in the appointment.",
      product_type: "SERVICE",
      price_ugx: 30000,
      vat_category: "EXEMPT",
      availability: "In Stock",
      available_slots: 10,
      image_filename: "example-consultation.jpg",
      min_order_qty: 1,
      notes: EXAMPLE_NOTE,
    },
  ];
  XLSX.utils.book_append_sheet(
    wb,
    dataSheet(
      PRODUCT_COLUMNS,
      [],
      examples,
      (key, r) => PRODUCT_FORMULAS[key]?.(L, r),
      PRODUCT_FORMULA_ROWS
    ),
    PRODUCTS_SHEET
  );

  XLSX.utils.book_append_sheet(
    wb,
    textSheet(LISTS(categoryNames, unitNames)),
    LISTS_SHEET
  );

  // The formulas read the stand-in cover file name from the READ ME.
  wb.Workbook = {
    ...(wb.Workbook || {}),
    Names: [{ Name: NAMES.STAND_IN_COVER, Ref: readme.standInRef }],
  };

  return wb;
};

/** Filename for a download. Vendor-specific when we know the vendor. */
export const templateFileName = (vendorName) =>
  templateFileNameFor(vendorName, "Lusuku_Healthcare_Import_Template.xlsx");

/** Browser-side download. No-op outside a browser. */
export const downloadTemplateWorkbook = (opts = {}) => {
  downloadWorkbook(buildTemplateWorkbook(opts), templateFileName(opts.vendorName));
};

export default buildTemplateWorkbook;
